import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loading } from "@/components/pawkt/Empty";
import { useOutboundVisible } from "@/components/outbound/shared";
import { StatusPill } from "@/components/receptionist/ConnectReceptionistCard";
import { Discovery } from "@/components/receptionist/Discovery";
import { ShallyOneClick } from "@/components/receptionist/ShallyOneClick";
import { ShallyOnboarding } from "@/components/receptionist/ShallyOnboarding";
import { ShallyGreetingCard } from "@/components/receptionist/ShallyGreetingCard";
import { BookOpen, Mic2, PhoneOutgoing, CreditCard, ChevronRight, Sparkles, Volume2, PhoneCall, Users, Lock, Wrench } from "lucide-react";
import { Panel, Chip } from "@/components/shally/ui";
import { RecordingSettings, SettingRow } from "@/components/shally/RecordingSettings";

function LinkRow({ to, icon: Icon, title, desc, testid }) {
  return (
    <Link to={to} data-testid={testid} className="group flex items-center gap-3 rounded-lg px-2 py-3 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground"><Icon className="h-4 w-4" /></span>
      <span className="min-w-0 flex-1"><span className="block text-sm font-semibold text-foreground">{title}</span><span className="block text-xs text-muted-foreground">{desc}</span></span>
      <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
    </Link>);
}

function CallHandling() {
  const ob = useOutboundVisible();
  return (
    <Panel title="Call handling" icon={PhoneCall} testid="shally-sec-handling">
      <div className="-mx-2 divide-y divide-border">
        <LinkRow to="/settings/ai-receptionist/knowledge" icon={BookOpen} title="Business knowledge" desc="Facts Shally may share with callers, beyond your services and hours." testid="shally-link-knowledge" />
        <LinkRow to="/settings/ai-receptionist/live" icon={Mic2} title="Talk to Shally" desc="Try a voice conversation in your browser. No phone call is made." testid="shally-link-live" />
        {ob === true && <LinkRow to="/settings/ai-receptionist/outbound" icon={PhoneOutgoing} title="Outbound AI calling" desc="Reminder and follow-up calls made by Shally." testid="shally-link-outbound" />}
        <LinkRow to="/settings/ai-receptionist/plans" icon={CreditCard} title="Plan & minutes" desc="Your AI receptionist plan, trial and included minutes." testid="shally-link-plans" />
      </div>
    </Panel>);
}

function Access() {
  const rows = [["View calls, summaries and follow-ups", "Owner and admins. Managers only if you grant it in Roles."], ["Play, download or delete recordings", "Owner and admins. Each can be granted separately."],
    ["Change recording and retention", "Owner and admins only."], ["Change AI receptionist setup", "Owner and admins only."]];
  return (
    <Panel title="Access permissions" icon={Lock} testid="shally-sec-access" action={<Link to="/roles" className="text-xs font-semibold text-primary hover:underline" data-testid="shally-link-roles">Manage roles</Link>}>
      <div className="divide-y divide-border">{rows.map(([t, d]) => <SettingRow key={t} icon={Users} title={t} desc={d} />)}</div>
    </Panel>);
}

function SecHead({ id, title, desc }) {
  return <div id={id} className="scroll-mt-24 pt-2"><h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">{title}</h2>{desc && <p className="mt-0.5 text-sm text-muted-foreground">{desc}</p>}</div>;
}

const SECTIONS = [["general", "General"], ["voice", "Voice & language"], ["handling", "Call handling"], ["recording", "Recording & privacy"], ["access", "Access"], ["advanced", "Advanced"]];

const FIX = { hours: "/settings/business", services: "/services", staff: "/staff", subscription: "/settings/ai-receptionist/plans", minutes: "/settings/ai-receptionist/plans" };
const EMPTY = { connection_method: "", business_number: "", greeting: "", language: "en", transfer_number: "" };
const fmt = (n) => (n ? `+${n.slice(0, 2)} ${n.slice(2)}` : "");

function Step({ n, title, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`rcp-step-${n}`}>
      <h2 className="mb-3 font-semibold"><span className="mr-2 text-violet-600">{n}.</span>{title}</h2>
      {children}
    </section>
  );
}

function Connection({ methods, f, set }) {
  return (
    <div className="space-y-2">
      {Object.entries(methods).map(([k, d]) => (
        <label key={k} className={`flex cursor-pointer gap-3 rounded-xl border p-3 text-sm ${f.connection_method === k ? "border-violet-500 bg-violet-50" : "border-slate-200"}`} data-testid={`rcp-method-${k}`}>
          <input type="radio" checked={f.connection_method === k} onChange={() => set({ ...f, connection_method: k })} />
          <span><b>{{ forward: "A. Existing number (call forwarding)", connected: "B. Compatible connected number", new: "C. New AI receptionist number" }[k]}</b><br /><span className="text-slate-500">{d}</span></span>
        </label>
      ))}
      {["forward", "connected"].includes(f.connection_method) && (
        <Input value={f.business_number} onChange={(e) => set({ ...f, business_number: e.target.value })} placeholder="Business phone number your customers call, e.g. +91 20 1234 5678" data-testid="rcp-business-number" />
      )}
    </div>
  );
}

function Settings({ f, set, canTransfer }) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <label className="text-sm md:col-span-2">Greeting (optional)
        <Input value={f.greeting} maxLength={300} onChange={(e) => set({ ...f, greeting: e.target.value })} placeholder="Default: Hello, thank you for calling <your business>…" data-testid="rcp-greeting" /></label>
      <label className="text-sm">Language
        <select value={f.language} onChange={(e) => set({ ...f, language: e.target.value })} className="mt-1 h-9 w-full rounded-md border px-2" data-testid="rcp-language">
          <option value="en">Start in English, switch to Hindi automatically</option>
          <option value="hi">Start in Hindi, switch to English automatically</option>
        </select></label>
      <label className="text-sm">Human transfer number
        <Input value={f.transfer_number} onChange={(e) => set({ ...f, transfer_number: e.target.value })} placeholder="Indian staff number" data-testid="rcp-transfer" />
        {!canTransfer && <span className="text-xs text-amber-700" data-testid="rcp-transfer-plan">Your plan does not include live human transfers; callers asking for a person get a call-back. It is still used when minutes run out.</span>}</label>
      <p className="text-xs text-slate-500 md:col-span-2">Speaking voice: English or Hindi. Speech recognition uses English (en-US) only; Hindi speech recognition is not supported yet.</p>
    </div>
  );
}

function Plan({ e }) {
  const ob = useOutboundVisible();
  if (!e) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid="rcp-plan-box">
      <span>{e.ok ? <><b>{e.plan.name}</b>{e.kind === "trial" ? ` trial · ${e.expiry_label}` : ""} · {e.minutes_remaining} of {e.minutes_included} AI minutes left</> : e.reason}</span>
      <span className="flex gap-2"><Link className="font-semibold text-violet-700 underline" to="/settings/ai-receptionist/plans" data-testid="rcp-plan-link">{e.ok ? "Plans" : "Choose a plan"}</Link>
        <Link className="font-semibold text-violet-700 underline" to="/settings/ai-receptionist/reports" data-testid="rcp-report-link">Usage & billing</Link>
        <Link className="font-semibold text-violet-700 underline" to="/settings/ai-receptionist/knowledge" data-testid="rcp-knowledge-link">Business knowledge</Link>
        <Link className="font-semibold text-violet-700 underline" to="/settings/ai-receptionist/live" data-testid="rcp-live-link">Talk to Shally (voice test)</Link>
        {ob === true && <Link className="font-semibold text-violet-700 underline" to="/settings/ai-receptionist/outbound" data-testid="rcp-outbound-link">Outbound AI Calling</Link>}</span>
    </div>);
}

function Services() {
  const { data } = useQuery({ queryKey: ["rcp-services"], queryFn: async () => (await api.get("/services")).data, retry: false });
  const rows = (Array.isArray(data) ? data : data?.items || []).filter((s) => s.active !== false);
  return (
    <div className="text-sm" data-testid="rcp-services">
      {rows.length ? <ul className="grid gap-1 md:grid-cols-2">{rows.slice(0, 20).map((s) => <li key={s.id} className="rounded-md bg-slate-50 px-2 py-1">{s.name} · {s.price ? `₹${s.price}` : "no price set"}</li>)}</ul>
        : <p className="text-amber-800">No active services yet. The receptionist only quotes services and prices you have set.</p>}
      <p className="mt-2 text-xs text-slate-500">The receptionist never invents services, prices, hours or availability. Change them in <Link className="underline" to="/services">Services</Link> and <Link className="underline" to="/settings/business">Business settings → Hours</Link>.</p>
    </div>);
}

function Readiness({ v }) {
  return (
    <div className="space-y-2 text-sm" data-testid="rcp-readiness">
      <p>Status: <StatusPill s={v.status} testid="rcp-status" /></p>
      <p data-testid="rcp-number">AI receptionist number: {v.numbers.length ? v.numbers.map((n) => `${fmt(n.number)} (${n.enabled ? "active" : "not active"})`).join(", ") : "not mapped yet"}</p>
      {v.issues.length ? (
        <ul className="space-y-1">{v.issues.map((i) => (
          <li key={i.key} className="rounded-md bg-amber-50 px-2.5 py-1.5 text-amber-900" data-testid={`rcp-issue-${i.key}`}>{i.msg}{FIX[i.key] && <Link className="ml-2 font-semibold underline" to={FIX[i.key]}>Fix</Link>}</li>))}</ul>
      ) : <p className="text-emerald-700" data-testid="rcp-all-ready">All readiness checks pass.</p>}
      <p className="text-xs text-slate-500">Opening hours, services and staff come from your existing Pawkt settings. Bookings are always pending requests for staff approval.</p>
    </div>
  );
}

function TestMode({ bid, can }) {
  const [tid, setTid] = useState(""), [log, setLog] = useState([]), [say, setSay] = useState(""), [busy, setBusy] = useState(false);
  useEffect(() => { setTid(""); setLog([]); }, [bid]);
  const turn = async (speech) => {
    setBusy(true);
    try {
      const d = (await api.post(`/receptionist/setup/${bid}/test`, { test_id: tid, speech, caller: "" })).data;
      setTid(d.action === "listen" ? d.test_id : "");
      setLog((l) => [...l, ...(speech ? [["Caller", speech]] : []), ...d.say.map((s) => ["Receptionist", s]), ...(d.action !== "listen" ? [["System", d.action === "transfer" ? "Call would transfer to staff" : "Call would end"]] : [])]);
      setSay("");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  if (!can) return <p className="text-sm text-slate-500">Save your setup to try test mode.</p>;
  return (
    <div className="space-y-2 text-sm" data-testid="rcp-test">
      <p className="text-xs font-semibold text-amber-700" data-testid="rcp-test-not-gpt-live">Text preview only: this is not a GPT-Live voice test.</p>
      <p className="text-xs text-slate-500">Shows the call wording as text. No phone call, no booking request, not counted in call usage.</p>
      <div className="max-h-60 space-y-1 overflow-y-auto rounded-lg bg-slate-50 p-2" data-testid="rcp-test-log">{log.map(([w, t], i) => <p key={i}><b>{w}:</b> {t}</p>)}</div>
      {!tid ? <Button size="sm" variant="outline" disabled={busy} onClick={() => { setLog([]); turn(""); }} data-testid="rcp-test-start">Start text preview</Button> : (
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (say.trim()) turn(say.trim()); }}>
          <Input value={say} onChange={(e) => setSay(e.target.value)} placeholder="What the caller says…" data-testid="rcp-test-input" />
          <Button size="sm" disabled={busy} data-testid="rcp-test-send">Send</Button>
        </form>)}
    </div>
  );
}

export default function ReceptionistSetup() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["rcp-setup"], queryFn: async () => (await api.get("/receptionist/setup")).data });
  const [bid, setBid] = useState(""), [f, set] = useState(EMPTY), [busy, setBusy] = useState(false), [ok, setOk] = useState(false);
  const { data: ent } = useQuery({ queryKey: ["rcp-ent"], queryFn: async () => (await api.get("/receptionist/billing/status")).data, retry: false });
  const v = data?.branches.find((b) => b.branch.id === bid);
  useEffect(() => { if (data && !bid && data.branches.length) setBid(data.branches[0].branch.id); }, [data, bid]);
  useEffect(() => { if (v) set({ ...EMPTY, ...(v.setup || {}) }); }, [bid, v?.setup?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); qc.invalidateQueries({ queryKey: ["rcp-setup"] }); }
    catch (e) { const d = e?.response?.data?.detail; toast.error(d?.issues ? `${d.msg}: ${d.issues.map((i) => i.msg).join(" ")}` : apiErr(e)); }
    finally { setBusy(false); }
  };
  if (isLoading || !data) return <Loading />;
  const noBranch = !data.branches.length;
  return (
    <div className="grid gap-8 lg:grid-cols-[180px_minmax(0,1fr)]" data-testid="rcp-setup-page">
      <nav className="hidden lg:block" aria-label="Settings sections"><div className="sticky top-24 space-y-0.5">
        {SECTIONS.map(([id, l]) => <a key={id} href={`#shally-${id}`} data-testid={`shally-jump-${id}`} className="block rounded-md px-2.5 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground">{l}</a>)}
      </div></nav>
      <div className="min-w-0 max-w-4xl space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h2 className="text-base font-bold text-foreground md:text-lg">Settings</h2><p className="text-sm text-muted-foreground">Set up how Shally answers, what it knows and what it records.</p></div>
        {data.branches.length > 1 && (
          <label className="flex items-center gap-2 text-sm text-muted-foreground">Branch
            <select value={bid} onChange={(e) => setBid(e.target.value)} className="h-9 rounded-lg border border-border bg-card px-3 text-sm text-foreground" data-testid="shally-branch">
              {data.branches.map((b) => <option key={b.branch.id} value={b.branch.id}>{b.branch.name}</option>)}
            </select></label>)}
      </div>
      <SecHead id="shally-general" title="General" desc="Switch Shally on and finish the guided setup." />
      {noBranch && <p className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 text-sm text-foreground" data-testid="rcp-no-branch"><Chip kind="config" />Add a branch for your business first, then return here to connect Shally. <Link to="/settings/business" className="font-semibold text-primary hover:underline">Business settings</Link></p>}
      {bid && <ShallyOneClick bid={bid} />}
      {bid && <ShallyOnboarding bid={bid} />}
      <SecHead id="shally-voice" title="Voice & language" desc="The greeting callers hear first. Language and greeting text are under Advanced → step 3." />
      <ShallyGreetingCard />
      <Panel title="Voice and language" icon={Volume2} testid="shally-sec-voice">
        <div className="divide-y divide-border">
          <SettingRow icon={Sparkles} title="Caller language" chip={<Chip kind="enabled" />} desc="Shally replies in English, Hindi or Hinglish, following the caller." />
          <SettingRow icon={Wrench} title="Starting language, greeting text and human transfer" desc="Change these in Advanced settings → Greeting, language and transfer.">
            <a href="#shally-advanced" onClick={() => { const el = document.getElementById("shally-advanced"); if (el) { el.open = true; } }} className="text-xs font-semibold text-primary hover:underline" data-testid="shally-open-advanced">Open</a>
          </SettingRow>
        </div>
      </Panel>
      <SecHead id="shally-handling" title="Call handling" />
      <CallHandling />
      <SecHead id="shally-recording" title="Recording & privacy" desc="Recording is off unless you switch it on." />
      <RecordingSettings />
      <SecHead id="shally-access" title="Access" />
      <Access />
      <div id="shally-advanced-anchor" />
      {!noBranch && <details id="shally-advanced" className="group scroll-mt-24 rounded-xl border border-border bg-card p-4 shadow-card" data-testid="shally-advanced">
        <summary className="cursor-pointer list-none text-sm font-semibold text-foreground">Advanced settings <span className="font-normal text-muted-foreground">· phone connection, greeting, technical readiness and activation</span></summary>
        <div className="mt-4 space-y-4">
      <Step n={0} title="Plan or trial"><Plan e={ent} /></Step>
      <Step n={1} title="Branch">
        <select value={bid} onChange={(e) => setBid(e.target.value)} className="h-9 w-full rounded-md border px-2 text-sm" data-testid="rcp-branch">
          {data.branches.map((b) => <option key={b.branch.id} value={b.branch.id}>{b.branch.name}</option>)}
        </select>
      </Step>
      {v && <>
        <Step n={2} title="How calls reach the receptionist"><Connection methods={data.methods} f={f} set={set} /></Step>
        <Step n={3} title="Greeting, language and transfer"><Settings f={f} set={set} canTransfer={!!ent?.plan?.features?.transfer} />
          <Button className="mt-4" disabled={busy || !f.connection_method} onClick={() => run(() => api.put(`/receptionist/setup/${bid}`, f), "Draft saved (not active)")} data-testid="rcp-save">Save draft</Button>
        </Step>
        <Step n={4} title="Readiness and status"><Readiness v={v} /><div className="mt-3"><Discovery bid={bid} known={v.issues.map((i) => i.key)} /></div><div className="mt-3"><Services /></div><p className="mt-2 text-xs text-slate-500" data-testid="rcp-charges">Phone-number rental and transferred-call carrier charges are billed separately by the telephony provider and are not included in your plan.</p></Step>
        <Step n={5} title="Text preview (not a voice test)"><TestMode bid={bid} can={v.can_test} /></Step>
        <Step n={6} title="Activate"><div id="rcp-activate-step" />
          {v.numbers.some((n) => n.enabled)
            ? <Button variant="outline" disabled={busy} onClick={() => run(() => api.post(`/receptionist/setup/${bid}/activate`, { enabled: false }), "Receptionist turned off")} data-testid="rcp-deactivate">Turn off receptionist</Button>
            : <>
              <label className="mb-2 flex items-start gap-2 text-sm" data-testid="rcp-confirm-label"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="rcp-confirm" />
                I confirm the setup above is correct and want the live AI receptionist to answer calls on this number.</label>
              <Button disabled={busy || !v.can_activate || !ok} onClick={() => run(() => api.post(`/receptionist/setup/${bid}/activate`, { enabled: true, confirm: true }), "Receptionist is active")} data-testid="rcp-activate">Activate receptionist</Button></>}
          {!v.can_activate && !v.numbers.some((n) => n.enabled) && <p className="mt-2 text-xs text-slate-500">Activation unlocks when a receptionist number is mapped to this branch and every readiness check passes.</p>}
        </Step>
      </>}
        </div>
      </details>}
      </div>
    </div>
  );
}
